import { Box } from "@mittwald/flow-components/dist/components/Box";
import Breakpoints from "@mittwald/flow-components/dist/components/Breakpoints";
import { Form, useForm } from "@mittwald/flow-components/dist/components/Form";
import invariant from "invariant";
import React from "react";
import { v4 as UUIDv4 } from "uuid";
import z from "zod";
import {
  Article,
  ArticleFactory,
  ArticleTemplateName,
} from "../../../model/article";
import DedicatedServerArticle from "../../../model/article/DedicatedServerArticle";
import Bytes from "../../../model/misc/Bytes";
import ArticleUI from "../../../model/ui/article/ArticleUI";
import * as Styled from "../../components/styled";
import { getPromoCodeFromPath } from "../../utils/promoCode";
import { withValidatedProps } from "../../withValidatedProps";
import { orderDedicatedServerUrlParam } from "../OrderDedicatedServerWidget/orderDedicatedServerUrlParam";
import ConfigurationSection from "./components/ConfigurationSection";
import CtaSection from "./components/CtaSection";
import type { FormFields } from "./types";

export default withValidatedProps(
  z.object({
    orderDedicatedServerWidgetUrl: z.string(),
  }),
  (props) => {
    const dedicatedServerArticles = ArticleFactory.useLoadAllByTemplateAndTag(
      ArticleTemplateName.vServerHosting,
      DedicatedServerArticle.articleTag,
    );

    const defaultSelectedArticle =
      dedicatedServerArticles.find((a) => ArticleUI.of(a).isPromoted) ??
      dedicatedServerArticles.find((a) => a.id === "DS25-0006");

    if (!defaultSelectedArticle) {
      throw new Error("No dedicated server articles available");
    }

    Article.assertType(defaultSelectedArticle, DedicatedServerArticle);

    const mustGetArticle = (id: string): DedicatedServerArticle => {
      const article = dedicatedServerArticles.find((a) => a.id === id);
      invariant(!!article, `Article ${id} not found`);
      Article.assertType(article, DedicatedServerArticle);
      return article;
    };

    const promotion = getPromoCodeFromPath();

    const form = useForm<FormFields>({
      defaultValues: {
        articleId: defaultSelectedArticle.id,
        resourceFilter: "ram-optimized",
        storageSizeInBytes:
          defaultSelectedArticle.baseStorageAttribute.bytes.bytes,
        useFreeTrial: true,
      },
      onSubmit: (values) => {
        const storageSizeInGiB = Bytes.of(
          values.storageSizeInBytes,
          "bytes",
        ).in("GiB");

        const encoded = orderDedicatedServerUrlParam.encode({
          storageSizeInGiB,
          articleId: values.articleId,
          freeTrial: values.useFreeTrial,
          transactionId: UUIDv4(),
          promotion: promotion?.promoCode,
          promotionDays: promotion?.promotionDays,
        });

        const url = new URL(
          `${window.location.protocol}//${window.location.host}`,
        );
        url.pathname = props.orderDedicatedServerWidgetUrl;
        url.searchParams.set("p", encoded);

        window.location.href = url.toString();
      },
    });

    const selectedArticle = mustGetArticle(form.watch("articleId"));
    const freeTrial = form.watch("useFreeTrial");

    return (
      <Form controller={form}>
        <Styled.ContentBox>
          <Breakpoints.Box breakpoints={[800]}>
            <Box _flexDirection={["column", "row"]} _itemGap="xl">
              <Box _flexBasis="60%">
                <ConfigurationSection article={selectedArticle} />
              </Box>
              <Styled.OffsetBox breakpointClassName={["narrow", "wide"]}>
                <CtaSection
                  article={selectedArticle}
                  freeTrial={freeTrial}
                  promotion={promotion}
                />
              </Styled.OffsetBox>
            </Box>
          </Breakpoints.Box>
        </Styled.ContentBox>
      </Form>
    );
  },
);
